import { Component, prop } from "@kluntje/core";
import ConsentManagementService from "Services/ConsentManagementService";
import { ViewportObserver } from "@kluntje/services";
import CookieService from "Services/CookieService";

interface VisitedCookie {
  lastVisit: Date;
  lastTimeOpened?: Date;
  pageViewsPerDay: number;
  answered: boolean;
  closed: number;
  askLater: boolean;
}

class FRAPopup extends Component {
  private cookieConsentGiven?: boolean;
  private parsedCookie?: VisitedCookie;
  viewportObserver = ViewportObserver.getInstance();

  @prop({ required: true })
  feedbackUrl = "";

  @prop({ required: false })
  userConsent = false;

  constructor() {
    super({
      ui: {
        overlay: ".lsg-js-overlay--boxed-content :-one",
        button: ".fra-e-button :-one",
        askLater: ".fra-e-link :-one",
        closeIcon: ".fra-m-overlay__close-icon :-one",
      },
      events: [
        {
          event: "kl-in-vp",
          target: "this",
          handler: "handleInViewport",
        },
        {
          event: "click",
          target: "button",
          handler: "handleButtonClick",
        },
        {
          event: "click",
          target: "askLater",
          handler: "handleAskLaterClick",
        },
        {
          event: "click",
          target: "closeIcon",
          handler: "handleCloseClick",
        },
        {
          event: "load",
          target: "window",
          handler: "handlePageView",
        },
      ],
    });
  }

  /**
   * @override
   */
  async afterComponentRender() {
    if (this.userConsent) {
      this.viewportObserver.observe(this);
      this.cookieConsentGiven = true;
    } else {
      await ConsentManagementService.whenUCReady();
      const ucServices = await ConsentManagementService.getUCServices();
      this.cookieConsentGiven = ucServices.some(s => s.categorySlug === "marketing" && s.consent.status);
      if (this.cookieConsentGiven) this.viewportObserver.observe(this);
    }
  }

  handleInViewport(): void {
    this.handleCookieData();
  }

  /*
  updates page views per visit. A page view counts if last page load is within 24h of last visit
   */
  handlePageView() {
    if (CookieService.isCookieSet("visited")) {
      this.parseCookie();
      if (this.parsedCookie) {
        const hoursSinceLastVisit = Math.floor(
          Math.abs(new Date(this.parsedCookie.lastVisit).getTime() - new Date().getTime()) / 1000 / 60 / 60,
        );
        this.parsedCookie.lastVisit = new Date();
        if (hoursSinceLastVisit < 24) {
          this.parsedCookie.pageViewsPerDay++;
          CookieService.setCookie("visited", JSON.stringify(this.parsedCookie));
        } else {
          // resetting page views
          this.parsedCookie.pageViewsPerDay = 1;
          CookieService.setCookie("visited", JSON.stringify(this.parsedCookie));
        }
      }
    } else if (this.cookieConsentGiven) {
      const initCookie: VisitedCookie = {
        answered: false,
        closed: 0,
        pageViewsPerDay: 1,
        lastVisit: new Date(),
        lastTimeOpened: undefined,
        askLater: false,
      };
      CookieService.setCookie("visited", JSON.stringify(initCookie));
    }
  }

  handleCookieData() {
    if (
      this.parsedCookie &&
      this.ui.overlay &&
      !this.parsedCookie.answered &&
      this.parsedCookie.closed < 2 &&
      this.parsedCookie.pageViewsPerDay > 1 &&
      this.validateLastTimeOpened()
    ) {
      this.parsedCookie.lastTimeOpened = new Date();
      this.ui.overlay.open();
      window.dataLayer?.push({
        event: "gaEvent",
        eventCategory: "survey",
        eventAction: "Open Overlay",
        eventLabel: "Open Overlay",
      });
    }
  }

  /*
  lastTimeOpened has to be at least 72h in the past or undefined or 24h in the past if askLater is true
   */
  validateLastTimeOpened(): boolean {
    if (this.parsedCookie) {
      if (this.parsedCookie.lastTimeOpened) {
        const hoursSinceLastTimeOpened = Math.floor(
          Math.abs(new Date(this.parsedCookie.lastTimeOpened).getTime() - new Date().getTime()) / 1000 / 60 / 60,
        );
        if (hoursSinceLastTimeOpened >= 72) return true;
        return hoursSinceLastTimeOpened >= 24 && this.parsedCookie.askLater;
      }
    }
    return true;
  }

  handleButtonClick(): void {
    if (this.parsedCookie) {
      this.parsedCookie.answered = true;
      CookieService.setCookie("visited", JSON.stringify(this.parsedCookie));
      window.dataLayer?.push({
        event: "gaEvent",
        eventCategory: "survey",
        eventAction: this.feedbackUrl,
        eventLabel: "Open Survey",
      });
    }
    if (this.ui.overlay) this.ui.overlay.close();
    window.open(this.feedbackUrl, "_blank");
  }

  handleCloseClick(): void {
    if (this.parsedCookie) {
      this.parsedCookie.closed++;
      CookieService.setCookie("visited", JSON.stringify(this.parsedCookie));
      window.dataLayer?.push({
        event: "gaEvent",
        eventCategory: "survey",
        eventAction: "Close Button",
        eventLabel: "Close Button",
      });
    }
  }

  handleAskLaterClick(): void {
    if (this.parsedCookie) {
      this.parsedCookie.askLater = true;
      CookieService.setCookie("visited", JSON.stringify(this.parsedCookie));
    }
    if (this.ui.overlay) {
      this.ui.overlay.close();
      window.dataLayer?.push({
        event: "gaEvent",
        eventCategory: "survey",
        eventAction: "Ask later Button",
        eventLabel: "Ask Later",
      });
    }
  }

  parseCookie(): void {
    const cookie = CookieService.getCookie("visited");
    if (cookie && !this.parsedCookie) {
      this.parsedCookie = JSON.parse(cookie);
    }
  }
}

customElements.define("fra-m-popup", FRAPopup);

export default FRAPopup;
